<script setup lang="ts">
import ProjectAvatar from "@/components/projects/ProjectAvatar.vue";
import DwgIcon from "@/components/UI/icons/DwgIcon.vue";
import DRBtn from "@/components/UI/DRBtn.vue";
import { drawingRepository } from "@/repositories/drawing.repository";
import { projectRepository } from "@/repositories/project.repository";
import type { DRDrawing, DRLocalDrawingData, DRPartialProject } from "@/types";
import { computed, ref, watch } from "vue";
import UserState from "@/stores/UserState";
import { useTranslation } from "i18next-vue";

const { t } = useTranslation();

const props = defineProps<{
  localData: DRLocalDrawingData;
}>();

const getDrawing = async (id: number) => {
  return await drawingRepository.getById(id);
};
const getProjects = async (id: number) => {
  return await projectRepository.getById(id);
};

const drawing = ref<DRDrawing | undefined>(undefined);
const project = ref<DRPartialProject | undefined>(undefined);
const showNotInOrgModal = ref(false);

const currentOrganizationId = computed(() => UserState.data?.organization?.id);

// Check if drawing belongs to current organization
const belongsToCurrentOrg = computed(() => {
  if (!drawing.value) return false;
  return drawing.value.organizationId === currentOrganizationId.value;
});

const openNotInOrgModal = () => {
  showNotInOrgModal.value = true;
};

watch(
  () => props.localData.id,
  async () => {
    drawing.value = await getDrawing(props.localData.id);
  },
  { immediate: true }
);

watch(
  () => drawing.value?.projectId,
  async () => {
    if (drawing.value?.projectId) {
      project.value = await getProjects(drawing.value.projectId);
    }
  },
  { immediate: true }
);
</script>

<template>
  <li
    class="hoverable-white rounded p-2 d-inline-flex justify-content-between align-items-center hoverable-white border-0 position-relative"
  >
    <!-- Overlay for drawings not in current organization -->
    <div
      v-if="!belongsToCurrentOrg"
      class="not-in-org-overlay d-flex align-items-center justify-content-center"
    >
      <span
        class="not-in-org-message px-3 py-2 rounded text-center cursor-pointer"
        @click="openNotInOrgModal"
      >
        {{ t("dashboard:sync.notInOrgShort") }}
      </span>
    </div>

    <div
      class="flex-grow-1 overflow-hidden text-truncate d-flex gap-2 align-items-center"
      :class="{ 'content-blurred': !belongsToCurrentOrg }"
    >
      <div class="col-auto"><DwgIcon :size="20" /></div>
      <div class="flew-grow-1 text-truncate">
        <div class="d-flex text-truncate">
          <div class="col-12 text-truncate">
            {{ drawing?.name }}
          </div>
        </div>
      </div>
    </div>
    <RouterLink
      v-if="project && belongsToCurrentOrg"
      :to="{
        name: 'project',
        params: { id: project.id },
      }"
      custom
      v-slot="{ href, navigate }"
    >
      <div
        class="cursor-pointer hoverable d-flex flex-row align-items-center gap-2 p-1 rounded gap-2"
        @click="navigate"
        :href="href"
      >
        <div class="ps-2">
          <ProjectAvatar v-if="project" :project="project" :size="20" />
        </div>
        <small class="text-muted align-self-center">
          {{ project?.name }}
        </small>
        <el-icon size="0.8rem"><i class="bi-chevron-right"></i></el-icon>
      </div>
    </RouterLink>

    <!-- Modal for not in organization details -->
    <el-dialog
      v-model="showNotInOrgModal"
      :title="t('dashboard:sync.notInOrgModal.title')"
      width="400px"
    >
      <p>{{ t("dashboard:sync.notInOrgModal.description") }}</p>
      <div class="d-flex flex-column gap-2 mt-3">
        <div class="d-flex justify-content-between">
          <span class="text-muted">{{
            t("dashboard:sync.notInOrgModal.drawingId")
          }}</span>
          <span class="fw-medium">#{{ drawing?.id }}</span>
        </div>
        <div class="d-flex justify-content-between">
          <span class="text-muted">{{
            t("dashboard:sync.notInOrgModal.drawingOrg")
          }}</span>
          <span class="fw-medium">#{{ drawing?.organizationId }}</span>
        </div>
        <div class="d-flex justify-content-between">
          <span class="text-muted">{{
            t("dashboard:sync.notInOrgModal.yourOrg")
          }}</span>
          <span class="fw-medium">#{{ currentOrganizationId }}</span>
        </div>
      </div>
      <template #footer>
        <DRBtn
          variant="secondary"
          :label="t('common:close')"
          @click="showNotInOrgModal = false"
        />
      </template>
    </el-dialog>
  </li>
</template>

<style lang="scss" scoped>
.not-in-org-overlay {
  position: absolute;
  inset: 0;
  z-index: 10;
  background-color: transparent;
  pointer-events: none;
}

.not-in-org-message {
  background-color: var(--bs-body-bg);
  color: var(--bs-body-color);
  font-size: 0.85rem;
  border: 1px solid var(--bs-border-color);
  pointer-events: auto;
}

.content-blurred {
  filter: blur(4px);
  user-select: none;
}
</style>
